import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3000';

/**
 * Budget Service - API calls for budget and recurring expense management
 */
export const budgetService = {
    // ==================== BUDGETS ====================

    /**
     * Set budget for a category
     */
    async setBudget(category, year, month, amount) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ category, year, month, amount })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to set budget');
            return data;
        } catch (error) {
            console.error('Error setting budget:', error);
            throw error;
        }
    },

    /**
     * Set annual budget for a category (auto-splits across months)
     */
    async setAnnualBudget(category, year, annualAmount) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/annual`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ category, year, annual_amount: annualAmount })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to set annual budget');
            return data;
        } catch (error) {
            console.error('Error setting annual budget:', error);
            throw error;
        }
    },

    /**
     * Get budget for a specific category/period
     */
    async getBudget(category, year, month) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/${category}/${year}/${month}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get budget');
            return data;
        } catch (error) {
            console.error('Error getting budget:', error);
            throw error;
        }
    },

    /**
     * Get all budgets for a period
     */
    async getBudgetsForPeriod(year, month) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/period/${year}/${month}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get budgets for period');
            return data;
        } catch (error) {
            console.error('Error getting budgets for period:', error);
            throw error;
        }
    },

    /**
     * Check if expense would exceed budget
     */
    async checkBudgetAvailability(category, amount, year = null, month = null) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/check-availability`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ category, amount, year, month })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to check budget availability');
            return data;
        } catch (error) {
            console.error('Error checking budget availability:', error);
            throw error;
        }
    },

    // ==================== RECURRING EXPENSES ====================

    /**
     * Create a recurring expense
     */
    async createRecurringExpense(expenseData) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/recurring`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(expenseData)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to create recurring expense');
            return data;
        } catch (error) {
            console.error('Error creating recurring expense:', error);
            throw error;
        }
    },

    /**
     * Get all recurring expenses
     */
    async getRecurringExpenses(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.category) params.append('category', filters.category);

            const response = await fetch(`${API_BASE_URL}/budgets/recurring?${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get recurring expenses');
            return data;
        } catch (error) {
            console.error('Error getting recurring expenses:', error);
            throw error;
        }
    },

    /**
     * Get due recurring expenses
     */
    async getDueRecurringExpenses() {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/recurring/due`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get due recurring expenses');
            return data;
        } catch (error) {
            console.error('Error getting due recurring expenses:', error);
            throw error;
        }
    },

    /**
     * Process all due recurring expenses
     */
    async processRecurringExpenses() {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/recurring/process`, {
                method: 'POST',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to process recurring expenses');
            return data;
        } catch (error) {
            console.error('Error processing recurring expenses:', error);
            throw error;
        }
    },

    /**
     * Manually generate expense from recurring template
     */
    async generateFromRecurring(recurringId) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/recurring/${recurringId}/generate`, {
                method: 'POST',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to generate expense');
            return data;
        } catch (error) {
            console.error('Error generating expense:', error);
            throw error;
        }
    },

    /**
     * Pause a recurring expense
     */
    async pauseRecurring(recurringId) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/recurring/${recurringId}/pause`, {
                method: 'POST',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to pause recurring expense');
            return data;
        } catch (error) {
            console.error('Error pausing recurring expense:', error);
            throw error;
        }
    },

    /**
     * Resume a paused recurring expense
     */
    async resumeRecurring(recurringId) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/recurring/${recurringId}/resume`, {
                method: 'POST',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to resume recurring expense');
            return data;
        } catch (error) {
            console.error('Error resuming recurring expense:', error);
            throw error;
        }
    },

    /**
     * Cancel a recurring expense
     */
    async cancelRecurring(recurringId) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/recurring/${recurringId}/cancel`, {
                method: 'POST',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to cancel recurring expense');
            return data;
        } catch (error) {
            console.error('Error cancelling recurring expense:', error);
            throw error;
        }
    },

    // ==================== EXPENSE VAT ====================

    /**
     * Calculate VAT for an expense amount
     */
    async calculateExpenseVat(amount, vatRate = 14, isInclusive = true) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/expense-vat/calculate`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ amount, vat_rate: vatRate, is_inclusive: isInclusive })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to calculate expense VAT');
            return data;
        } catch (error) {
            console.error('Error calculating expense VAT:', error);
            throw error;
        }
    },

    /**
     * Add VAT calculation to an existing expense
     */
    async addVatToExpense(expenseId, vatRate = 14, isInclusive = true) {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/expense-vat/${expenseId}`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ vat_rate: vatRate, is_inclusive: isInclusive })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to add VAT to expense');
            return data;
        } catch (error) {
            console.error('Error adding VAT to expense:', error);
            throw error;
        }
    },

    /**
     * Get default budget configuration
     */
    async getDefaultConfig() {
        try {
            const response = await fetch(`${API_BASE_URL}/budgets/config/defaults`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get default config');
            return data;
        } catch (error) {
            console.error('Error getting default config:', error);
            throw error;
        }
    }
};
